/**
 * Livre des jalons (D§4 : « liste complète, progression, gemmes gagnées »).
 *
 * Le HUD ne montre que LE prochain jalon ; soixante-dix jalons encaissés restaient un
 * palmarès que personne ne voyait. Ce panneau vit dans **Progression** : un résumé
 * chiffré (encaissés, gemmes gagnées), puis le prochain objectif de chaque famille
 * avec sa barre — jamais la liste brute des soixante-dix, qui se ferait défiler sans
 * se lire.
 *
 * L'échelle des jalons est infinie par construction (§1) : chaque famille a donc
 * TOUJOURS un prochain à montrer, sauf les familles finies (premier bâtiment de chaque
 * type, premier prestige) qui affichent alors leur accomplissement.
 */

import type { CitySnapshot } from '@/core/gameState';
import { buildingCounts } from '@/core/gameState';
import { MILESTONES, milestoneById, nextMilestoneOfKind } from '@/core/milestones';
import type { MilestoneKind, MilestoneProgress } from '@/core/milestones';
import { n, t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { createPanel, createProgress } from './components';
import type { ProgressHandle } from './components';
import { icon } from './icons';

export interface MilestoneBookHandle {
  readonly el: HTMLElement;
  update(snapshot: CitySnapshot): void;
}

/** Les familles montrées, dans l'ordre de lecture — du quotidien au long terme. */
const KINDS: readonly MilestoneKind[] = [
  'first-building',
  'production',
  'land',
  'visitors',
  // ROADMAP 3.2 — trois familles de plus, dans le même ordre de lecture : du geste
  // quotidien (la rue qui s'étend) au très long terme (les ères).
  'roads',
  'decor',
  'prestige',
  'eras',
];

interface KindRow {
  readonly el: HTMLElement;
  readonly label: HTMLElement;
  readonly count: HTMLElement;
  readonly progress: ProgressHandle;
}

export function createMilestoneBook(): MilestoneBookHandle {
  const title = document.createElement('h3');
  title.className = 'milestone-book__title';
  title.append(icon('book'), t('milestoneBook.title'));

  /* ── Le palmarès en une ligne ────────────────────────────────────────── */
  const summary = document.createElement('p');
  summary.className = 'milestone-book__summary';

  /* ── Une ligne par famille : le prochain objectif, avec sa barre ─────── */
  const list = document.createElement('div');
  list.className = 'milestone-book__list';

  /*
    ── LE DÉTAIL D'UNE FAMILLE (ROADMAP 3.2) ─────────────────────────────────

    Le livre ne montrait que le PROCHAIN objectif de chaque famille. Cent
    vingt-trois jalons existent, et le joueur n'en voyait jamais ni ce qu'il avait
    accompli, ni ce qui venait après le suivant — donc rien de ce qui donne envie de
    continuer (§1).

    ⚠️ **On n'affiche pas les cent vingt-trois d'un coup**, et l'en-tête de ce fichier
    disait déjà pourquoi : une liste brute se fait défiler sans se lire. On ouvre donc
    UNE famille à la fois, et à l'intérieur, une fenêtre : le prochain, ceux qui
    suivent, et le compte de ceux qui sont derrière — jamais une colonne de soixante.
  */
  let opened: MilestoneKind | null = null;

  const detail = document.createElement('div');
  detail.className = 'milestone-book__detail';
  detail.hidden = true;

  const rows = new Map<MilestoneKind, KindRow>();
  for (const kind of KINDS) {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'milestone-book__row';
    row.addEventListener('click', () => {
      // Retaper la famille ouverte la referme : le geste attendu, et il évite d'avoir
      // à viser une croix.
      opened = opened === kind ? null : kind;
      repaint();
    });

    const label = document.createElement('span');
    label.className = 'milestone-book__label';

    const count = document.createElement('span');
    count.className = 'milestone-book__count';

    const head = document.createElement('div');
    head.className = 'milestone-book__head';
    head.append(label, count);

    const progress = createProgress({ value: 0, max: 1, domain: 'gems' });

    row.append(head, progress.el);
    list.append(row);
    rows.set(kind, { el: row, label, count, progress });
  }

  const panel = createPanel({ children: [title, summary, list, detail] });
  panel.el.classList.add('milestone-book');

  /*
    Le dernier état connu, pour que le clic sur une famille puisse repeindre sans
    attendre le tick suivant. Un panneau qui ne réagit qu'au prochain rafraîchissement
    se lit comme un panneau cassé (mesuré sur les onglets de la 0.63.0).
  */
  let last: CitySnapshot | null = null;

  /** Repeint le détail de la famille ouverte, s'il y en a une. */
  function repaint(): void {
    for (const [kind, row] of rows) {
      row.el.classList.toggle('milestone-book__row--open', kind === opened);
      row.el.setAttribute('aria-expanded', String(kind === opened));
    }

    detail.hidden = opened === null || last === null;
    detail.replaceChildren();
    if (opened === null || last === null) return;

    const claimed = new Set(last.claimedMilestones);
    const family = MILESTONES.filter((milestone) => milestone.kind === opened);
    const done = family.filter((milestone) => claimed.has(milestone.id));

    /*
      ⚠️ **Ce qui est masqué se DIT.** Une famille de production compte soixante
      paliers : en afficher soixante ferait la colonne illisible que ce fichier refuse
      depuis le début, mais en cacher cinquante sans le dire laisserait croire que le
      jeu s'arrête là. On montre donc la fenêtre utile — les six prochains — et on
      annonce le reste par un compte.
    */
    const upcoming = family.filter((milestone) => !claimed.has(milestone.id));
    const shown = upcoming.slice(0, 6);

    const header = document.createElement('p');
    header.className = 'milestone-book__detail-head';
    header.textContent = t('milestoneBook.family', {
      done: n(done.length),
      total: n(family.length),
    });
    detail.append(header);

    for (const milestone of shown) {
      const line = document.createElement('div');
      line.className = 'milestone-book__item';

      const label = document.createElement('span');
      const param = milestone.labelParam;
      label.textContent = t(
        milestone.labelKey as TranslationKey,
        param === undefined
          ? {}
          : {
              value:
                typeof param === 'number' ? n(param) : t(`buildings.${param}` as TranslationKey),
            },
      );

      const reward = document.createElement('span');
      reward.className = 'milestone-book__reward';
      reward.append(icon('gem'), String(milestone.gems));

      line.append(label, reward);
      detail.append(line);
    }

    if (upcoming.length > shown.length) {
      const more = document.createElement('p');
      more.className = 'milestone-book__more';
      more.textContent = t('milestoneBook.more', {
        count: n(upcoming.length - shown.length),
      });
      detail.append(more);
    }
  }

  return {
    el: panel.el,

    update(snapshot) {
      last = snapshot;
      const claimed = new Set(snapshot.claimedMilestones);

      /*
        Les gemmes GAGNÉES se recomptent depuis les identifiants encaissés, jamais
        depuis un compteur parallèle : une seule source de vérité, celle qui crédite.
      */
      let earnedGems = 0;
      for (const id of claimed) earnedGems += milestoneById(id)?.gems ?? 0;

      summary.textContent = t('milestoneBook.summary', {
        count: n(claimed.size),
        total: n(MILESTONES.length),
        gems: n(earnedGems),
      });

      const progress: MilestoneProgress = {
        buildingCounts: buildingCounts(snapshot),
        landPurchases: snapshot.landPurchases,
        visitorsReceived: snapshot.visitorsReceived,
        totalProduced: snapshot.totalProduced,
        prestigeLevel: snapshot.prestigeLevel,
        roadCells: snapshot.islands[0]?.roads.length ?? 0,
        decorations: snapshot.islands[0]?.traces.length ?? 0,
      };

      for (const kind of KINDS) {
        const row = rows.get(kind);
        if (row === undefined) continue;

        const next = nextMilestoneOfKind(kind, progress, claimed);
        if (next === null) {
          // Famille épuisée (finie par construction) : l'accomplissement se dit,
          // il ne disparaît pas — c'est un palmarès, pas une liste de tâches.
          row.label.textContent = t(`milestoneBook.done.${kind}` as TranslationKey);
          row.count.textContent = '';
          row.progress.setValue(1);
          continue;
        }

        // Le paramètre est tantôt un nombre (paliers), tantôt un type de bâtiment
        // (premier exemplaire) — même résolution que le filet du HUD.
        const param = next.milestone.labelParam;
        row.label.textContent = t(
          next.milestone.labelKey as TranslationKey,
          param === undefined
            ? {}
            : {
                value:
                  typeof param === 'number' ? n(param) : t(`buildings.${param}` as TranslationKey),
              },
        );
        row.count.textContent = `${n(next.current)} / ${n(next.milestone.threshold)}`;
        row.progress.setValue(next.ratio);
      }

      repaint();
    },
  };
}
